import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { URLS } from 'constants/urls'
import {
  LOCATIONS_TABLE_ITEMS,
  TABLE_INFO,
  LOCATION_DELETE_CONFIRMATION,
} from 'texts/locationDetails'
import { useAppDispatch } from 'redux/hooks'
import { Confirmation } from 'ui/molecules/Confirmation'
import { useApiRequest } from 'hooks/useApiRequest'
import { setNotificationMessage } from 'redux/notifications/slice'
import { Typography } from '@frontend/design_system'
import { LocationsApiTypes, locationsApi } from 'api'
import { Table } from 'ui/molecules/Table'
import { SEQUENCE_NUMBER } from 'texts/uiTexts'
import { useCompanyId } from 'hooks/useCompanyId'
import styles from './styles.module.scss'
import { setPendingLocationsCount } from 'redux/locations/slice'
import { Loader } from 'ui/atoms/Loader'
import { TableTooltip } from 'ui/molecules/TableTooltip'

export const LocationsTable = () => {
  const { id } = useParams()
  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] =
    useState<LocationsApiTypes.ShortHirePointData | null>(null)
  const [locationsList, setLocationsList] =
    useState<LocationsApiTypes.GetHirePointListDataResponse | null>(null)
  const companyId = useCompanyId()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const deleteHirePointRequest = useApiRequest(
    (hirePointId) => locationsApi.deleteHirePoint(hirePointId),
    undefined,
    true
  )

  const locationsListRequest = useApiRequest((companyId) =>
    locationsApi.getHireBasicList({
      companyId: String(companyId),
    })
  )

  const getLocationsData = async (companyId: string | number) => {
    const response = await locationsListRequest.apiRequest(companyId)
    if (response && response.data) {
      setLocationsList(response.data)
      dispatch(setPendingLocationsCount(response.data.hirePoints.length))
    }
  }

  const handleRowClick = (id: number) => {
    navigate(
      `${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/${companyId}/${id}/edit`
    )
  }

  const handleDeleteLocation = (id: number) => {
    const location = locationsList?.hirePoints.find((item) => item.id === id)
    if (location) {
      setRemoveModalOpen(true)
      setSelectedLocation(location)
    }
  }

  const handleRemoveConfirm = async () => {
    if (selectedLocation && companyId) {
      const response = await deleteHirePointRequest.apiRequest(
        selectedLocation.id
      )
      if (response) {
        dispatch(
          setNotificationMessage({
            notificationMessage: LOCATION_DELETE_CONFIRMATION.SUCCESS,
          })
        )
        getLocationsData(companyId)
        if (id && selectedLocation?.id && Number(id) === selectedLocation.id) {
          navigate(`${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/add`)
        }
      }
      setRemoveModalOpen(false)
    }
  }

  const handleRemoveModalClose = () => {
    setSelectedLocation(null)
    setRemoveModalOpen(false)
  }

  const formattedBodyItems = useMemo(() => {
    if (!locationsList) return []
    return locationsList.hirePoints.map((item, i) => {
      const sequenceNumber = i + 1
      return {
        items: [
          sequenceNumber,
          item.country,
          item.city,
          item.locationName,
          item.street,
          item.building,
          <TableTooltip
            title={item.pickUpOption || '-'}
            onClick={() => handleRowClick(item.id)}
          />,
        ],
        onClick: () => {
          handleRowClick(item.id)
        },
        removeRowClick: () => {
          handleDeleteLocation(item.id)
        },
        isActive: item.id === Number(id),
      }
    })
  }, [locationsList])

  const formattedHeadItems = [SEQUENCE_NUMBER, ...LOCATIONS_TABLE_ITEMS].map(
    (el) => ({
      value: <Typography name="Subtitle3">{el}</Typography>,
    })
  )

  useEffect(() => {
    if (companyId) {
      getLocationsData(companyId)
    }
  }, [companyId])

  return (
    <>
      {isRemoveModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: `${LOCATION_DELETE_CONFIRMATION.CONFIRMATION_MODAL.QUESTION} ${selectedLocation?.locationName}?`,
            onConfirm: handleRemoveConfirm,
            onClose: handleRemoveModalClose,
            title: `${LOCATION_DELETE_CONFIRMATION.CONFIRMATION_MODAL.TITLE} ${selectedLocation?.locationName}?`,
            confirmLabel:
              LOCATION_DELETE_CONFIRMATION.CONFIRMATION_MODAL.BUTTON_LABEL,
          }}
        />
      )}
      <div className={styles['location-block']}>
        <div className={styles['location-header']}>
          <div className={styles['location-title']}>
            <Typography name="H5">{TABLE_INFO.TITLE}</Typography>
            <Typography name="Button2">{TABLE_INFO.SUBTITLE}</Typography>
          </div>
        </div>
        <div className={styles['info-table']}>
          <div className={styles.table}>
            {locationsListRequest.loading ? (
              <div className={styles.loader}>
                <Loader />
              </div>
            ) : (
              <Table
                headItems={formattedHeadItems}
                bodyItems={formattedBodyItems}
                isStickyHeader
                initItemsWidth={['53', '131', '131', '131', '131', '137']}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
